import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Loader2,
  Lock,
  LogOut,
  RefreshCw,
  Send,
  Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "srikanth_admin_token";

const STATUS_ORDER = ["new", "contacted", "won", "lost"];
const STATUS_STYLES = {
  new: "bg-[#E2FF31]/10 text-[#E2FF31] border-[#E2FF31]/30",
  contacted: "bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/30",
  won: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30",
  lost: "bg-white/10 text-white/60 border-white/20",
};

function useAuth() {
  const [token, setToken] = useState(
    () => localStorage.getItem(TOKEN_KEY) || ""
  );
  const login = (t) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };
  return { token, login, logout };
}

function LoginScreen({ onLogin }) {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, {
        token: value.trim(),
      });
      onLogin(data.token);
      toast.success("Signed in");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Sign-in failed";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md p-8 rounded-2xl border border-white/10 bg-[#0C0C0C]/80 backdrop-blur"
      >
        <div className="flex items-center gap-2 mb-6">
          <span className="inline-block w-2.5 h-2.5 bg-[#E2FF31] rounded-sm rotate-45" />
          <span className="font-display text-lg font-semibold tracking-tight">
            Elevate<span className="text-[#E2FF31]">.</span>studios
          </span>
          <span className="ml-auto font-mono-tag text-[10px] text-white/40">
            admin
          </span>
        </div>
        <h1 className="font-display text-3xl font-medium mb-2">
          Sign in to your dashboard
        </h1>
        <p className="text-white/60 text-sm mb-8">
          Enter the ADMIN_TOKEN from your backend&nbsp;.env file to continue.
        </p>
        <label
          htmlFor="admin-token"
          className="font-mono-tag text-[10px] text-white/40 block mb-2"
        >
          // Admin token
        </label>
        <div className="relative">
          <Lock
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
          />
          <input
            id="admin-token"
            data-testid="admin-login-token"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            type="password"
            autoComplete="current-password"
            required
            className="w-full bg-black border border-white/15 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-white/25 focus:border-[#E2FF31] focus:outline-none"
            placeholder="paste your admin token"
          />
        </div>
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={submitting}
          className="mt-6 w-full btn-primary justify-center disabled:opacity-70"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Verifying
            </>
          ) : (
            <>
              Sign in <ArrowUpRight size={16} />
            </>
          )}
        </button>
        <div className="mt-6 pt-6 border-t border-white/10 font-mono-tag text-[9px] text-white/40 leading-relaxed">
          Not sure what to enter? Open /app/backend/.env and set{" "}
          <span className="text-[#E2FF31]">ADMIN_TOKEN=</span> to a long random
          string, restart the backend, then use that value here.
        </div>
      </form>
    </div>
  );
}

function ChannelPill({ label, ok, sub }) {
  return (
    <div
      className={`p-4 rounded-xl border ${
        ok
          ? "border-[#10B981]/30 bg-[#10B981]/5"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`w-2 h-2 rounded-full ${
            ok ? "bg-[#10B981]" : "bg-white/30"
          }`}
        />
        <span className="font-mono-tag text-[10px] text-white/70">{label}</span>
      </div>
      <div className="mt-2 font-display text-base">
        {ok ? "Configured" : "Not configured"}
      </div>
      {sub && (
        <div className="mt-1 font-mono-tag text-[9px] text-white/40">{sub}</div>
      )}
    </div>
  );
}

function StatusChip({ status, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full border font-mono-tag text-[10px] transition-colors ${
        STATUS_STYLES[status] || STATUS_STYLES.new
      }`}
    >
      {status}
    </button>
  );
}

export default function Admin() {
  const { token, login, logout } = useAuth();
  const [config, setConfig] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [cfg, cts, lg] = await Promise.all([
        axios.get(`${API}/admin/config`, { headers: authHeader }),
        axios.get(`${API}/admin/contacts`, {
          headers: authHeader,
          params: filter ? { status: filter, limit: 200 } : { limit: 200 },
        }),
        axios.get(`${API}/admin/notifications`, {
          headers: authHeader,
          params: { limit: 20 },
        }),
      ]);
      setConfig(cfg.data);
      setContacts(cts.data.items || []);
      setTotal(cts.data.total || 0);
      setLogs(lg.data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter]);

  const changeStatus = async (id, next) => {
    try {
      await axios.patch(
        `${API}/admin/contacts/${id}`,
        { status: next },
        { headers: authHeader }
      );
      setContacts((cs) =>
        cs.map((c) => (c.id === id ? { ...c, status: next } : c))
      );
    } catch (err) {
      toast.error("Could not update status");
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/test-notification`,
        {},
        { headers: authHeader }
      );
      const results = data.results || [];
      if (results.length === 0) {
        toast.info(
          "No channels configured. Add WhatsApp or SMTP credentials in backend/.env."
        );
      } else {
        results.forEach((r) => {
          if (r.ok) toast.success(`Sent via ${r.channel}`);
          else
            toast.error(
              `${r.channel} failed: ${
                r.reason || r.error || r.status_code || "unknown"
              }`
            );
        });
      }
      load();
    } catch (err) {
      toast.error("Test send failed");
    } finally {
      setTesting(false);
    }
  };

  if (!token) return <LoginScreen onLogin={login} />;

  const wa = config?.notifications?.whatsapp || {};
  const email = config?.notifications?.email || {};

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-20">
      {/* Top bar */}
      <header
        data-testid="admin-header"
        className="sticky top-0 z-10 bg-black/70 backdrop-blur-xl border-b border-white/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 bg-[#E2FF31] rounded-sm rotate-45" />
            <span className="font-display text-base font-semibold">
              Elevate<span className="text-[#E2FF31]">.</span>studios
            </span>
            <span className="font-mono-tag text-[10px] text-white/40 ml-2">
              // admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="btn-ghost text-xs"
              disabled={loading}
            >
              <RefreshCw
                size={14}
                className={loading ? "animate-spin" : ""}
              />
              Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="btn-ghost text-xs"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 space-y-12">
        {/* Overview */}
        <section>
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="font-mono-tag text-[10px] text-[#E2FF31] mb-2">
                // Notification channels
              </div>
              <h1 className="font-display text-3xl font-medium">
                Delivery pipeline
              </h1>
            </div>
            <button
              data-testid="admin-test-notification"
              onClick={sendTest}
              disabled={testing}
              className="btn-primary text-sm"
            >
              {testing ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Sending
                </>
              ) : (
                <>
                  <Send size={14} /> Send test
                </>
              )}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ChannelPill
              label="WHATSAPP"
              ok={!!wa.configured}
              sub={
                wa.provider
                  ? `Provider: ${wa.provider}`
                  : "Set WHATSAPP_PROVIDER to meta or twilio"
              }
            />
            <ChannelPill
              label="EMAIL (SMTP)"
              ok={!!email.configured}
              sub="Set SMTP_* to enable email"
            />
            <ChannelPill
              label="MONGODB"
              ok={true}
              sub="All enquiries are always saved"
            />
          </div>
        </section>

        {/* Contacts */}
        <section data-testid="admin-contacts-section">
          <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
            <div>
              <div className="font-mono-tag text-[10px] text-[#E2FF31] mb-2">
                // Enquiries
              </div>
              <h2 className="font-display text-3xl font-medium">
                {total} lead{total === 1 ? "" : "s"}
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="admin-filter-all"
                onClick={() => setFilter("")}
                className={`px-3 py-1.5 rounded-full border font-mono-tag text-[10px] ${
                  !filter
                    ? "bg-[#E2FF31] text-black border-[#E2FF31]"
                    : "border-white/20 text-white/70"
                }`}
              >
                All
              </button>
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  data-testid={`admin-filter-${s}`}
                  onClick={() => setFilter(s)}
                  className={`px-3 py-1.5 rounded-full border font-mono-tag text-[10px] ${
                    filter === s
                      ? "bg-[#E2FF31] text-black border-[#E2FF31]"
                      : "border-white/20 text-white/70"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {contacts.length === 0 ? (
            <div className="p-10 rounded-2xl border border-white/10 bg-[#0C0C0C]/60 text-center text-white/50">
              No leads yet.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-white/50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-mono-tag text-[10px]">
                      When
                    </th>
                    <th className="px-4 py-3 font-mono-tag text-[10px]">
                      Name / Email
                    </th>
                    <th className="px-4 py-3 font-mono-tag text-[10px]">
                      Project
                    </th>
                    <th className="px-4 py-3 font-mono-tag text-[10px]">
                      Message
                    </th>
                    <th className="px-4 py-3 font-mono-tag text-[10px]">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr
                      key={c.id}
                      data-testid={`admin-contact-row-${c.id}`}
                      className="border-t border-white/5 hover:bg-white/[0.02]"
                    >
                      <td className="px-4 py-4 text-white/50 font-mono-tag text-[10px] whitespace-nowrap">
                        {new Date(c.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium">{c.name}</div>
                        <a
                          href={`mailto:${c.email}`}
                          className="text-[#E2FF31] hover:underline text-xs"
                        >
                          {c.email}
                        </a>
                      </td>
                      <td className="px-4 py-4 text-white/70">
                        {c.project_type || "—"}
                      </td>
                      <td className="px-4 py-4 text-white/70 max-w-md">
                        <div className="line-clamp-3">{c.message}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {STATUS_ORDER.map((s) => (
                            <StatusChip
                              key={s}
                              status={s}
                              testId={`admin-set-status-${c.id}-${s}`}
                              onClick={() => changeStatus(c.id, s)}
                            />
                          ))}
                          {c.status && (
                            <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-mono-tag text-white/60">
                              <Check size={10} className="text-[#E2FF31]" />
                              {c.status}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Notification log */}
        <section data-testid="admin-logs-section">
          <div className="font-mono-tag text-[10px] text-[#E2FF31] mb-2">
            // Recent notification attempts
          </div>
          <h2 className="font-display text-3xl font-medium mb-6">
            Delivery log
          </h2>
          {logs.length === 0 ? (
            <div className="p-6 rounded-2xl border border-white/10 bg-[#0C0C0C]/60 text-white/50 text-sm">
              No notification attempts yet. Once WhatsApp or SMTP is
              configured, sends will appear here.
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((l, i) => (
                <div
                  key={i}
                  className="p-4 rounded-xl border border-white/10 bg-[#0C0C0C]/60 flex flex-wrap items-center gap-3"
                >
                  <div className="font-mono-tag text-[10px] text-white/50 w-40">
                    {new Date(l.created_at).toLocaleString()}
                  </div>
                  <div className="font-mono-tag text-[10px] px-2 py-1 rounded border border-white/10 text-white/70">
                    {l.kind}
                  </div>
                  <div className="text-white/70 text-sm">
                    {l.payload_summary?.name} ·{" "}
                    <span className="text-white/50">
                      {l.payload_summary?.email}
                    </span>
                  </div>
                  <div className="ml-auto flex flex-wrap gap-2">
                    {(l.results || []).map((r, j) => (
                      <span
                        key={j}
                        className={`px-2 py-1 rounded-full border font-mono-tag text-[10px] ${
                          r.ok
                            ? "border-[#10B981]/30 bg-[#10B981]/10 text-[#10B981]"
                            : "border-red-500/30 bg-red-500/10 text-red-300"
                        }`}
                      >
                        {r.channel}: {r.ok ? "ok" : r.reason || "fail"}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
